import React, { useState, useEffect } from "react";
import "leaflet/dist/leaflet.css";
import ApiManager from "../../ApiManager/ApiManager";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getContrastColor } from "../../utils/GetContrastColor";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import MapInput from "../../components/MapInput";
import CustomSelect from "../../components/CustomSelect";
import { formatDateTime } from "../../utils/dateUtils";
import MapView from "../../components/MapView/MapView";

const AddBookedTrip = ({ tripInfo, setEditTrip, setRefreshKey }) => {
  const navigate = useNavigate();
  const {
    isLoading,
    loadingStates,
    loadingMessages,
    loadingProgress,
    withLoading,
    getProgress,
    getMessage,
  } = useComponentLoading({
    fetchCompanies: false,
    fetchDrivers: false,
    fetchCars: false,
    fetchCarTypes: false,
    fetchTripTypes: false,
    fetchLabels: false,
    fetchGarages: false,
    fetchCities: false,
    fetchBillingItems: false,
  });

  const [passengers, setPassengers] = useState(tripInfo?.passengers || []);
  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(tripInfo?.company?.id || "");
  const [drivers, setDrivers] = useState([]);
  const [cars, setCars] = useState([]);
  const [carType, setCarType] = useState(tripInfo?.carType || {});
  const [carTypes, setCarTypes] = useState([]);
  const [tripTypes, setTripTypes] = useState([]);
  const [tripType, setTripType] = useState(tripInfo?.tripType || {});
  const [charges, setCharges] = useState(tripInfo?.charges || {});
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingCarTypes, setLoadingCarTypes] = useState(false);
  const [loadingTripTypes, setLoadingTripTypes] = useState(false);
  const [loadingLables, setLoadingLabels] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]); // State for selected tags
  const [loadingPrice, setLoadingPrice] = useState(false); // State to track loading
  const [items, setItems] = useState([{ cost: "", id: 0 }]);
  const [billingItems, setBillingItems] = useState([]);
  const [labels, setLabels] = useState([]); // Stores all labels
  const [selectedLabelIds, setSelectedLabelIds] = useState(
    tripInfo.labels?.map((label) => label.id) || []
  );
  const [showPrices, setShowPrices] = useState(false);
  const [isInputFocusedReporting, setIsInputFocusedReporting] = useState(false);
  const [isInputFocusedDrop, setIsInputFocusedDrop] = useState(false);
  const [selectedBillingItems, setSelectedBillingItems] = useState(
    tripInfo.billingItems || []
  );
  const [billingItemPayload, setBillingItemPayload] = useState(
    tripInfo.billingItems || []
  );
  const [cities, setCities] = useState([]);
  const [fromCity, setFromCity] = useState(tripInfo.fromCity || "");
  const [toCity, setToCity] = useState(tripInfo.toCity || "");
  const [garageData, setGarageData] = useState([]);
  const [selectedGarage, setSelectedGarage] = useState(
    tripInfo?.garage?.id || ""
  );

  // City location state for autocomplete
  const [fromCityLocation, setFromCityLocation] = useState(null);
  const [toCityLocation, setToCityLocation] = useState(null);
  const [contractAutoSwitchEnabled, setContractAutoSwitchEnabled] = useState(
    typeof tripInfo?.contractAutoSwitchEnabled === "boolean"
      ? tripInfo.contractAutoSwitchEnabled
      : false
  );

  const [reportingAddressSuggestions, setReportingAddressSuggestions] =
    useState([]);
  const [dropAddressSuggestions, setDropAddressSuggestions] = useState([]);

  const handleTripTypeChange = (e) => {
    e.preventDefault();
    const selectedTripTypeId = parseInt(e.target.value);
    const selectedType = tripTypes.find(
      (type) => type.id === selectedTripTypeId
    );
    setTripType(selectedType);
  };

  const handleReportingAddressSelect = (location) => {
    setMapReportingAddress(location);
  };

  const handleDropAddressSelect = (location) => {
    setMapDropAddress(location);
  };

  const [mapReportingAddress, setMapReportingAddress] = useState({
    latitude: tripInfo.reportingLocation?.latitude,
    longitude: tripInfo.reportingLocation?.longitude,
    description: tripInfo.reportingAddress || "",
  });

  const [mapDropAddress, setMapDropAddress] = useState({
    latitude: tripInfo.dropLocation?.latitude,
    longitude: tripInfo.dropLocation?.longitude,
    description: tripInfo.dropAddress || "",
  });

  const handleFileUpload = async (file, index) => {
    if (!file) return;

    try {
      // Delete existing file if there is one
      const existingFile = selectedBillingItems[index]?.imagePath;
      if (existingFile) {
        await deleteStoredFile(existingFile);
      }

      // Get a presigned URL for upload
      const presignedResponse = await ApiManager.storageGetPresignedUploadUrl({
        filename: file.name,
        directory: "images/receipts",
      });

      if (!presignedResponse.ok) {
        throw new Error("Failed to get presigned URL");
      }

      const presignedData = await presignedResponse.json();

      // The response contains: url, fileName, key, expires
      console.log("Presigned URL data:", presignedData);

      // Upload the file directly to the storage provider
      const uploadResponse = await fetch(presignedData.url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }

      // Save metadata after upload using the fileName from the response
      const metadataResponse = await ApiManager.storageSaveMetadata(
        presignedData.fileName,
        presignedData.key
      );

      if (!metadataResponse.ok) {
        throw new Error("Failed to save metadata");
      }

      // Use the fileName from the response for the imagePath
      handleBillingItemChange(index, "imagePath", presignedData.fileName);
      console.log(
        "Upload successful. File will expire in:",
        presignedData.expires
      );
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file. Please try again.");
    }
  };

  const deleteStoredFile = async (fileName) => {
    try {
      const response = await ApiManager.storageDeleteFile(fileName);

      if (!response.ok) {
        throw new Error("Failed to delete file");
      }

      console.log(`File ${fileName} deleted successfully`);
    } catch (error) {
      console.error(`Error deleting file ${fileName}:`, error);
    }
  };

  const handleBillingItemChange = (index, field, value) => {
    const updatedBillingItemPayload = [...billingItemPayload];
    updatedBillingItemPayload[index][field] = value;
    setBillingItemPayload(updatedBillingItemPayload);

    const updatedBillingItems = [...selectedBillingItems];
    updatedBillingItems[index][field] = value;
    setSelectedBillingItems(updatedBillingItems);
  };

  const removeBillingItem = (index) => {
    const updatedBillingItemPayload = [...billingItemPayload];
    updatedBillingItemPayload[index]["cost"] = 0;
    setBillingItemPayload(updatedBillingItemPayload);

    const updatedBillingItems = selectedBillingItems.filter(
      (_, i) => i !== index
    );
    setSelectedBillingItems(updatedBillingItems);
  };

  const addBillingItem = () => {
    setSelectedBillingItems([
      ...selectedBillingItems,
      { billingItemId: "", cost: "", imagePath: "", notes: "" },
    ]);
    setBillingItemPayload([
      ...billingItemPayload,
      { billingItemId: "", cost: "", imagePath: "", notes: "" },
    ]);
  };

  const handleLabelSelect = (e) => {
    const selectedId = parseInt(e.target.value);
    if (!selectedLabelIds.includes(selectedId)) {
      setSelectedLabelIds([...selectedLabelIds, selectedId]);
    }
  };

  // Handle label removal
  const handleRemoveLabel = (id) => {
    setSelectedLabelIds(selectedLabelIds.filter((labelId) => labelId !== id));
  };

  // Handler for marker drag end
  const handleMarkerDragEnd = async (id, latlng) => {
    if (id === "reporting") {
      setMapReportingAddress((prev) => ({
        ...prev,
        latitude: latlng.latitude,
        longitude: latlng.longitude,
      }));
    } else if (id === "drop") {
      setMapDropAddress((prev) => ({
        ...prev,
        latitude: latlng.latitude,
        longitude: latlng.longitude,
      }));
    }
    
    // Fetch address suggestions
    try {
      const params = new URLSearchParams({
        lat: latlng.latitude,
        lng: latlng.longitude,
      });
      const response = await ApiManager.getReverseGeocode(params);
      if (response.ok) {
        const data = await response.json();
        if (
          data.success &&
          data.data &&
          Array.isArray(data.data.results) &&
          data.data.results.length > 0
        ) {
          if (id === "reporting") {
            setReportingAddressSuggestions(data.data.results);
          } else if (id === "drop") {
            setDropAddressSuggestions(data.data.results);
          }
        }
      }
    } catch (err) {
      setReportingAddressSuggestions([]);
      setDropAddressSuggestions([]);
    }
  };

  // Handler for selecting an address from suggestions (for MapInput)
  const handleSelectAddressSuggestion = (type, suggestion) => {
    if (type === "reporting") {
      setMapReportingAddress((prev) => ({
        ...prev,
        description: suggestion.formatted_address,
        latitude: suggestion.location.lat,
        longitude: suggestion.location.lng,
      }));
      setReportingAddressSuggestions([]);
    } else if (type === "drop") {
      setMapDropAddress((prev) => ({
        ...prev,
        description: suggestion.formatted_address,
        latitude: suggestion.location.lat,
        longitude: suggestion.location.lng,
      }));
      setDropAddressSuggestions([]);
    }
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        await withLoading(
          "fetchCompanies",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const response = await ApiManager.getAllCompanies(params);
            if (!response.ok) throw new Error("Failed to fetch companies");
            const data = await response.json();
            setCompanies(data || []);
          },
          "Fetching companies...",
          30000
        );
      } catch (error) {
        console.error("Error fetching companies:", error);
        toast.error("Failed to fetch companies");
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchCarTypes = async () => {
      try {
        await withLoading(
          "fetchCarTypes",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const response = await ApiManager.getCarType(params);
            if (!response.ok) throw new Error("Failed to fetch car types");
            const data = await response.json();
            setCarTypes(data || []);
          },
          "Fetching car types...",
          30000
        );
      } catch (error) {
        console.error("Error fetching car types:", error);
        toast.error("Failed to fetch car types");
      }
    };
    fetchCarTypes();
  }, []);

  useEffect(() => {
    const fetchTripTypes = async () => {
      try {
        await withLoading(
          "fetchTripTypes",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const response = await ApiManager.getAllTripTypes(params);
            if (!response.ok) throw new Error("Failed to fetch trip types");
            const data = await response.json();
            setTripTypes(data || []);
          },
          "Fetching trip types...",
          30000
        );
      } catch (error) {
        console.error("Error fetching trip types:", error);
        toast.error("Failed to fetch trip types");
      }
    };
    fetchTripTypes();
  }, []);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        await withLoading(
          "fetchLabels",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const response = await ApiManager.getAllLabels(params);
            if (!response.ok) throw new Error("Failed to fetch labels");
            const data = await response.json();
            setLabels(data || []);
          },
          "Fetching labels...",
          30000
        );
      } catch (error) {
        console.error("Error fetching labels:", error);
        toast.error("Failed to fetch labels");
      }
    };
    fetchLabels();
  }, []);

  useEffect(() => {
    const fetchGarages = async () => {
      try {
        await withLoading(
          "fetchGarages",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const response = await ApiManager.getAllGarages(params);
            if (!response.ok) throw new Error("Failed to fetch garages");
            const data = await response.json();
            setGarageData(data || []);
          },
          "Fetching garages...",
          30000
        );
      } catch (error) {
        console.error("Error fetching garages:", error);
        toast.error("Failed to fetch garages");
      }
    };
    fetchGarages();
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        await withLoading(
          "fetchCities",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const response = await ApiManager.getAllCities(params);
            if (!response.ok) throw new Error("Failed to fetch cities");
            const data = await response.json();
            setCities(data || []);
          },
          "Fetching cities...",
          30000
        );
      } catch (error) {
        console.error("Error fetching cities:", error);
        toast.error("Failed to fetch cities");
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    const fetchBillingItems = async () => {
      try {
        await withLoading(
          "fetchBillingItems",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const response = await ApiManager.getAllBillingItems(params);
            if (!response.ok) throw new Error("Failed to fetch billing items");
            const data = await response.json();
            setBillingItems(data || []);
          },
          "Fetching billing items...",
          30000
        );
      } catch (error) {
        console.error("Error fetching billing items:", error);
        toast.error("Failed to fetch billing items");
      }
    };
    fetchBillingItems();
  }, []);

  useEffect(() => {
    const fetchDriversAndCars = async () => {
      if (tripInfo.driver && tripInfo.tripStatus === "ALLOTTED") {
        try {
          await withLoading(
            "fetchDrivers",
            async () => {
              const params = new URLSearchParams({
                page_size: 1000,
                page: 1,
              });
              const response = await ApiManager.getAllDrivers(params);
              if (!response.ok) throw new Error("Failed to fetch drivers");
              const data = await response.json();
              setDrivers(data || []);
            },
            "Fetching drivers...",
            30000
          );

          await withLoading(
            "fetchCars",
            async () => {
              const params = new URLSearchParams({
                page_size: 1000,
                page: 1,
              });
              const response = await ApiManager.getAllCars(params);
              if (!response.ok) throw new Error("Failed to fetch cars");
              const data = await response.json();
              setCars(data || []);
            },
            "Fetching cars...",
            30000
          );
        } catch (error) {
          console.error("Error fetching drivers and cars:", error);
          toast.error("Failed to fetch drivers and cars");
        }
      }
    };
    fetchDriversAndCars();
  }, [tripInfo.driver, tripInfo.tripStatus]);

  const addPassenger = () => {
    setPassengers([...passengers, { name: "", phone: "", email: "" }]);
  };

  const addAdditionalContact = () => {
    setAdditionalContacts([
      ...additionalContacts,
      { name: "", phone: "", email: "" },
    ]);
  };

  const removeAdditionalContact = (index) => {
    const updatedContacts = additionalContacts.filter((_, i) => i !== index);
    setAdditionalContacts(updatedContacts);
  };

  const handleAdditionalContactChange = (index, field, value) => {
    const updatedContacts = [...additionalContacts];
    updatedContacts[index][field] = value;
    setAdditionalContacts(updatedContacts);
  };

  const removePassenger = (index) => {
    const updatedPassengers = passengers.filter((_, i) => i !== index);
    setPassengers(updatedPassengers);
  };

  const handlePassengerChange = (index, field, value) => {
    const updatedPassengers = [...passengers];
    updatedPassengers[index][field] = value;
    setPassengers(updatedPassengers);
  };

  const handleGetPrice = async (event) => {
    setShowPrices(true);
    setLoadingPrice(true); // Start loading
    event.preventDefault(); // Prevent form submission reload

    const form = event.target.closest("form"); // This gets the closest form element
    if (!form) {
      console.error("Form not found");
      setLoadingPrice(false);
      return;
    }
    const formData = new FormData(form);

    try {
      const queryParams = new URLSearchParams({
        trip_type_id: parseInt(
          formData.get("tripType") ?? tripInfo?.tripType?.id
        ),
        car_type_id: parseInt(formData.get("carType") ?? tripInfo?.carType?.id),
        company_id: company,
        city: fromCity,
      });
      const response = await ApiManager.getCharges(queryParams);

      if (!response.ok) {
        const errorText = await response.text();
        toast.error(`Failed to fetch price: ${errorText}`);
        throw new Error("Failed to fetch price");
      }
      const data = await response.json(); // Parse the JSON response
      setCharges(data);
      form.querySelector('[name="price"]').value =
        data?.rates?.base_rates || "";
      form.querySelector('[name="perExKmRate"]').value =
        data.rates?.extra_km_rates || "";
      form.querySelector('[name="perExTimeUnitRate"]').value =
        tripType && tripType.type === "day-km"
          ? data.rates?.extra_day_rates || ""
          : data.rates?.extra_hr_rates || "";
    } catch (err) {
      console.error("Error fetching price:", err);
      form.querySelector('[name="price"]').value = "";
      form.querySelector('[name="perExKmRate"]').value = "";
      form.querySelector('[name="perExTimeUnitRate"]').value = "";
    } finally {
      setLoadingPrice(false); // Stop loading
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation for MapInput fields
    const reportingAddressValid =
      mapReportingAddress.description ||
      (mapReportingAddress.addressLine1 &&
        mapReportingAddress.city &&
        mapReportingAddress.state);
    const dropAddressValid =
      mapDropAddress.description ||
      (mapDropAddress.addressLine1 &&
        mapDropAddress.city &&
        mapDropAddress.state);

    if (!reportingAddressValid) {
      toast.error("Reporting address is required");
      return;
    }
    try {
      await withLoading(
        "submitForm",
        async (updateProgress) => {
          updateProgress(10, "Processing form data...");
          const formData = new FormData(e.target);
          const tripData = Object.fromEntries(formData.entries());

          const reportingAddress = mapReportingAddress.description;
          const dropAddress = mapDropAddress.description;

          // Format date and time to UNIX timestamp
          const scheduledStartTime =
            new Date(tripData.scheduledStartTime).getTime() / 1000;

          updateProgress(30, "Preparing trip data...");
          const formattedTripData = {
            tripId: tripInfo.tripId,
            companyId: company, // Assuming it's an ID
            fromCity: fromCity,
            toCity: toCity,
            // shortReportingAddress: tripData.shortReportingAddress,
            carType: parseInt(tripData?.carType), // Assuming it's an ID
            tripType: parseInt(tripData.tripType ?? tripInfo?.tripType?.id), // Assuming it's an ID
            garageId: parseInt(selectedGarage),
            scheduledStartTime: scheduledStartTime,
            scheduledEndTime: tripData.scheduledEndTime
              ? new Date(tripData.scheduledEndTime).getTime() / 1000
              : null,
            reportingTime: tripData.reportingTime
              ? new Date(tripData.reportingTime).getTime() / 1000
              : null,
            reportingAddress: reportingAddress,
            dropAddress: dropAddress,
            passengers: passengers.filter((p) => p.name || p.phone || p.email), // Filter out empty passengers
            bookedBy: {
              name: tripData.bookedByName,
              phone: tripData.bookedByPhone,
              email: tripData.bookedByEmail,
            },
            // additionalContacts: additionalContacts.filter(c => c.name || c.phone || c.email), // Filter out empty contacts
            labels: selectedLabelIds,
            remarks: tripData.remarks || "",
            operatorNotes: tripData.operatorNotes || "",
            contractAutoSwitchEnabled: contractAutoSwitchEnabled,
            ...(((tripInfo.charges &&
              Object.keys(tripInfo.charges).length > 0) ||
              showPrices) && {
              charges: {
                ...charges,
                rates: {
                  ...charges.rates,
                  base_rates: parseFloat(tripData.price) || 0,
                  extra_km_rates: parseFloat(tripData.perExKmRate) || 0,
                  ...(tripType?.type === "day-km" && {
                    extra_day_rates:
                      parseFloat(tripData.perExTimeUnitRate) || 0,
                  }),
                  ...(tripType?.type !== "day-km" && {
                    extra_hr_rates: parseFloat(tripData.perExTimeUnitRate) || 0,
                  }),
                },
              },
            }),
            reportingCoordinates: {
              latitude: mapReportingAddress.latitude,
              longitude: mapReportingAddress.longitude,
            },
            dropCoordinates: {
              latitude: mapDropAddress.latitude,
              longitude: mapDropAddress.longitude,
            },
            TripBillingItems: billingItemPayload
              .filter((item) => item.billingItemId) // Ensure billingItemId exists
              .map((item) => ({
                ...(item.id !== null && item.id !== undefined
                  ? { id: Number(item.id) }
                  : {}),
                billingItemId: Number(item.billingItemId),
                cost: parseFloat(item.cost),
                imagePath: item.imagePath || "",
                notes: item.notes || "",
              })),
          };

          updateProgress(50, "Updating trip details...");
          console.log("Formatted Trip Data:", formattedTripData);
          console.log(
            "Final JSON:",
            JSON.stringify(formattedTripData, null, 2)
          );

          const response = await ApiManager.updateTrip(null, formattedTripData);

          if (!response.ok) {
            const errorData = await response.json();
            console.error("Trip creation failed:", errorData);
            updateProgress(70, "Error updating trip. Handling errors...");
            throw new Error(errorData.message || "Failed to update trip");
          }

          const data = await response.json(); // Parse trip creation response
          console.log("Fetched data:", data);
          console.log("Trip ID:", data.trip.tripId);

          updateProgress(80, "Booking the trip...");
          const res = await ApiManager.bookTrip(data.trip.tripId);

          if (!res.ok) {
            const errorData = await res.json();
            console.error("Trip booking failed:", errorData);
            updateProgress(90, "Error booking trip. Handling errors...");
            throw new Error(errorData.message || "Failed to book trip");
          }

          updateProgress(100, "Trip updated and booked successfully!");

          navigate(`/login/trips/booking-details/${data.trip.tripId}`, {
            state: {
              showToast: true,
              toastMessage: "Trip updated and booked successfully!",
            },
          });
        },
        "Processing and submitting trip data..."
      );
    } catch (error) {
      console.error("Network Error:", error);
      toast.error(error.message || "Something went wrong. Please try again.");
    } finally {
      setEditTrip(false);
      setRefreshKey((prev) => prev + 1);
    }
  };

  const showToast = (message) => {
    toast.success(message, {
      position: "top-center", //toast.POSITION.TOP_CENTER,
      autoClose: 3000, // 3 seconds
    });
  };

  const fetchFile = async (imagePath) => {
    if (!imagePath) return;

    try {
      // Get a presigned URL for direct download
      const response = await ApiManager.storageGetPresignedDownloadUrl(
        imagePath
      );

      if (!response.ok) {
        throw new Error("Failed to generate presigned URL");
      }

      const result = await response.json();

      // Open the presigned URL directly in a new tab
      window.open(result.url, "_blank");
    } catch (error) {
      console.error("Error fetching file:", error);
    }
  };

  if (
    isLoading("fetchCompanies") ||
    isLoading("fetchDrivers") ||
    isLoading("fetchCars") ||
    isLoading("fetchCarTypes") ||
    isLoading("fetchTripTypes") ||
    isLoading("fetchLabels") ||
    isLoading("fetchGarages") ||
    isLoading("fetchCities")
  ) {
    return (
      <div className="trip-details-loading">
        <LoadingSpinner
          overlay={false}
          message={
            getMessage("fetchCompanies") ||
            getMessage("fetchDrivers") ||
            getMessage("fetchCars") ||
            getMessage("fetchCarTypes") ||
            getMessage("fetchTripTypes") ||
            getMessage("fetchLabels") ||
            getMessage("fetchGarages") ||
            getMessage("fetchCities")
          }
          progress={
            getProgress("fetchCompanies") ||
            getProgress("fetchDrivers") ||
            getProgress("fetchCars") ||
            getProgress("fetchCarTypes") ||
            getProgress("fetchTripTypes") ||
            getProgress("fetchLabels") ||
            getProgress("fetchGarages") ||
            getProgress("fetchCities")
          }
          showProgress={true}
        />
      </div>
    );
  }

  return (
    <div className="addNewTrip">
      <div>
        <h3>Book Trip</h3>
      </div>
      <form onSubmit={handleSubmit}>
        {tripInfo.tripStatus != "COMPLETED" && (
          <fieldset className="fieldset">
            <legend>Company Info</legend>
            <div className="formGroup">
              <label>Company *</label>
              <CustomSelect
                name="company"
                options={(companies || []).map((company) => ({
                  value: company.id,
                  label: company.name,
                }))}
                value={company}
                onChange={(selectedOption) =>
                  setCompany(selectedOption?.value || "")
                }
                placeholder="Select a Company"
                isSearchable={true}
                required
              />
            </div>
          </fieldset>
        )}

        {tripInfo.tripStatus != "COMPLETED" && (
          <fieldset className="fieldset">
            <legend>Booked By</legend>
            <div className="row">
              <div className="formGroup">
                <label>Name</label>
                <input
                  type="text"
                  name="bookedByName"
                  className="input"
                  defaultValue={tripInfo?.bookedBy?.name}
                />
              </div>
              <div className="formGroup">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="bookedByPhone"
                  defaultValue={tripInfo?.bookedBy?.phone}
                  className="input"
                  pattern="[0-9]{10}" // For a 10-digit number
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                    if (e.target.value.length > 10) {
                      e.target.value = e.target.value.slice(0, 10); // Limit to 10 digits
                    }
                  }}
                />
              </div>
              <div className="formGroup">
                <label>Email</label>
                <input
                  type="email"
                  name="bookedByEmail"
                  defaultValue={tripInfo?.bookedBy?.email}
                  className="input"
                />
              </div>
            </div>
          </fieldset>
        )}

        <fieldset className="fieldset">
          <legend>Passenger Details</legend>

          {(passengers || []).map((passenger, index) => (
            <div key={index} className="passengerRow">
              <div className="formGroup">
                <label>Passenger Name</label>
                <input
                  type="text"
                  value={passenger.name}
                  onChange={(e) =>
                    handlePassengerChange(index, "name", e.target.value)
                  }
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Passenger Phone Number</label>
                <input
                  type="tel"
                  value={passenger.phone}
                  onChange={(e) =>
                    handlePassengerChange(index, "phone", e.target.value)
                  }
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Passenger Email</label>
                <input
                  type="email"
                  value={passenger.email}
                  onChange={(e) =>
                    handlePassengerChange(index, "email", e.target.value)
                  }
                  className="input"
                />
              </div>
              <button
                type="button"
                onClick={() => removePassenger(index)}
                className="remove-button"
              >
                x
              </button>
            </div>
          ))}

          {(additionalContacts || []).map((contact, index) => (
            <div key={index} className="passengerRow">
              <div className="formGroup">
                <label>Contact Name</label>
                <input
                  type="text"
                  value={contact.name}
                  onChange={(e) =>
                    handleAdditionalContactChange(index, "name", e.target.value)
                  }
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Contact Phone Number</label>
                <input
                  type="tel"
                  value={contact.phone}
                  onChange={(e) =>
                    handleAdditionalContactChange(
                      index,
                      "phone",
                      e.target.value
                    )
                  }
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Contact Email</label>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) =>
                    handleAdditionalContactChange(
                      index,
                      "email",
                      e.target.value
                    )
                  }
                  className="input"
                />
              </div>
              <button
                type="button"
                onClick={() => removeAdditionalContact(index)}
                className="remove-button"
              >
                x
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addPassenger}
            className="custom-button"
          >
            + Add Another Passenger
          </button>
        </fieldset>

        {tripInfo.tripStatus != "COMPLETED" && (
          <fieldset className="fieldset">
            <legend>Address Details</legend>
            <div className="row">
              <div className="formGroup">
                <label>From City *</label>
                <CustomSelect
                  name="fromCity"
                  options={(cities || []).map((city) => ({
                    value: city.name,
                    label: city.name,
                  }))}
                  value={fromCity}
                  onChange={(selectedOption) => {
                    setFromCity(selectedOption?.value || "");
                    setFromCityLocation(selectedOption?.location || null);
                  }}
                  placeholder="Select a city"
                  required
                />
              </div>
              <div className="formGroup">
                <label>To City</label>
                <CustomSelect
                  name="toCity"
                  options={(cities || []).map((city) => ({
                    value: city.name,
                    label: city.name,
                  }))}
                  value={toCity}
                  onChange={(selectedOption) => {
                    setToCity(selectedOption?.value || "");
                    setToCityLocation(selectedOption?.location || null);
                  }}
                  placeholder="Select a city"
                  required
                />
              </div>
            </div>

            <div className="mapContainer">
              {/* Reporting Address Section */}
              <div>
                <MapInput
                  label="Reporting Address"
                  onAddressSelect={handleReportingAddressSelect}
                  initialValue={mapReportingAddress}
                  required={true}
                  name="reportingAddress"
                  validateValue={(value) => {
                    if (
                      typeof value === "object" &&
                      !value.description &&
                      !value.addressLine1
                    ) {
                      return "Reporting address is required";
                    }
                    return null;
                  }}
                  addressSuggestions={reportingAddressSuggestions}
                  onSelectAddressSuggestion={(suggestion) =>
                    handleSelectAddressSuggestion("reporting", suggestion)
                  }
                />
              </div>

              {/* Drop Address Section */}
              <div>
                <MapInput
                  label="Drop Address"
                  onAddressSelect={handleDropAddressSelect}
                  initialValue={mapDropAddress}
                  name="dropAddress"
                  validateValue={(value) => {
                    if (
                      typeof value === "object" &&
                      !value.description &&
                      !value.addressLine1
                    ) {
                      return "Drop address is required";
                    }
                    return null;
                  }}
                  addressSuggestions={dropAddressSuggestions}
                  onSelectAddressSuggestion={(suggestion) =>
                    handleSelectAddressSuggestion("drop", suggestion)
                  }
                />
              </div>

            </div>

            {/* Combined MapView for Reporting and Drop Address */}
            {tripInfo.tripStatus != "COMPLETED" && 
             (mapReportingAddress?.latitude || mapDropAddress?.latitude) && (
              <div className="mapContainer">
                <MapView
                  key="addBookedTrip-map"
                  markers={[
                    mapReportingAddress &&
                    typeof mapReportingAddress.latitude === "number" &&
                    typeof mapReportingAddress.longitude === "number" &&
                    !isNaN(mapReportingAddress.latitude) &&
                    !isNaN(mapReportingAddress.longitude)
                      ? {
                          latitude: mapReportingAddress.latitude,
                          longitude: mapReportingAddress.longitude,
                          label: "Reporting Address",
                          draggable: true,
                          id: "reporting",
                        }
                      : null,
                    mapDropAddress &&
                    typeof mapDropAddress.latitude === "number" &&
                    typeof mapDropAddress.longitude === "number" &&
                    !isNaN(mapDropAddress.latitude) &&
                    !isNaN(mapDropAddress.longitude)
                      ? {
                          latitude: mapDropAddress.latitude,
                          longitude: mapDropAddress.longitude,
                          label: "Drop Address",
                          draggable: true,
                          id: "drop",
                        }
                      : null,
                  ].filter(Boolean)}
                  zoom={13}
                  height={450}
                  onMarkerDragEnd={handleMarkerDragEnd}
                  showDirections={true}
                />
              </div>
            )}
          </fieldset>
        )}

        {tripInfo.tripStatus != "COMPLETED" && (
          <>
            <fieldset className="fieldset">
              <legend>Trip Details</legend>
              {/* First Row */}
              <div className="row">
                <div className="formGroup">
                  <label>Trip Type *</label>
                  <CustomSelect
                    name="tripType"
                    options={(tripTypes || []).map((tripType) => ({
                      value: tripType.id,
                      label: tripType.name,
                    }))}
                    value={tripType?.id}
                    onChange={(selectedOption) => {
                      const selectedType = tripTypes.find(
                        (type) => type.id === selectedOption?.value
                      );
                      setTripType(selectedType);
                    }}
                    placeholder="Select a Trip Type"
                    isDisabled={!!tripInfo?.tripType}
                    required
                  />
                </div>
                <div className="formGroup">
                  <label>Car Type *</label>
                  <CustomSelect
                    name="carType"
                    options={(carTypes || []).map((carType) => ({
                      value: carType.id,
                      label: carType.name,
                    }))}
                    value={carType?.id || ""}
                    onChange={(selectedOption) => {
                      const selectedType = carTypes.find(
                        (type) => type.id === selectedOption?.value
                      );
                      setCarType(selectedType);
                    }}
                    placeholder="Select a Car Type"
                    isSearchable={true}
                    required
                  />
                </div>
                <div className="formGroup">
                  <label htmlFor="contractAutoSwitchEnabledToggle">
                    Contract Auto Switch
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <label className="toggle-switch">
                      <input
                        id="contractAutoSwitchEnabledToggle"
                        type="checkbox"
                        checked={contractAutoSwitchEnabled}
                        onChange={() =>
                          setContractAutoSwitchEnabled((prev) => !prev)
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span>{contractAutoSwitchEnabled ? "On" : "Off"}</span>
                  </div>
                </div>
              </div>

              <div className="formGroup">
                <label>Garage</label>
                <CustomSelect
                  name="garage"
                  options={(garageData || []).map((garage) => ({
                    value: garage.id,
                    label: garage.description,
                  }))}
                  value={selectedGarage}
                  onChange={(selectedOption) =>
                    setSelectedGarage(selectedOption?.value || "")
                  }
                  placeholder="Select Garage"
                />
              </div>

              {/* Second Row */}
              <div className="row">
                <div className="formGroup">
                  <label>Scheduled Start Time *</label>
                  <input
                    type="datetime-local"
                    name="scheduledStartTime"
                    defaultValue={formatDateTime(
                      tripInfo?.scheduledStartTime,
                      "datetime-local"
                    )}
                    required
                    className="input"
                  />
                </div>
                <div className="formGroup">
                  <label>Scheduled End Time</label>
                  <input
                    type="datetime-local"
                    name="scheduledEndTime"
                    className="input"
                    defaultValue={formatDateTime(
                      tripInfo?.scheduledEndTime,
                      "datetime-local"
                    )}
                  />
                </div>
                <div className="formGroup">
                  <label>Reporting Time</label>
                  <input
                    type="datetime-local"
                    name="reportingTime"
                    defaultValue={formatDateTime(
                      tripInfo?.reportingTime,
                      "datetime-local"
                    )}
                    className="input"
                  />
                </div>
              </div>
            </fieldset>

            <fieldset className="fieldset">
              <legend>Charges</legend>

              <div className="row">
                {((tripInfo.charges &&
                  Object.keys(tripInfo.charges).length > 0) ||
                  showPrices) && (
                  <>
                    <div className="formGroup">
                      <label>Price*</label>
                      <input
                        type="text"
                        name="price"
                        defaultValue={tripInfo?.charges?.rates?.base_rates}
                        required={tripInfo.tripStatus != "CREATED"}
                        className="input"
                      />
                    </div>
                    <div className="formGroup">
                      <label>Per Extra KM rate*</label>
                      <input
                        type="text"
                        name="perExKmRate"
                        defaultValue={tripInfo?.charges?.rates?.extra_km_rates}
                        required={tripInfo.tripStatus != "CREATED"}
                        className="input"
                      />
                    </div>
                    <div className="formGroup">
                      <label>
                        {tripType && tripType.type === "day-km"
                          ? "Per Extra Day rate *"
                          : "Per Extra Hr rate *"}
                      </label>
                      <input
                        type="text"
                        name="perExTimeUnitRate"
                        defaultValue={
                          tripType.type !== "day-km"
                            ? tripInfo?.charges?.rates?.extra_hr_rates
                            : tripInfo?.charges?.rates?.extra_day_rates
                        }
                        required={tripInfo.tripStatus != "CREATED"}
                        className="input"
                      />
                    </div>
                  </>
                )}
                <div className="formGroup getPriceButton">
                  {/* <button type="button" className="btn" onClick={getPrice}>Get Price</button> */}
                  <button
                    type="button"
                    onClick={handleGetPrice}
                    className="custom-button"
                    disabled={loadingPrice} // Disable button while loading
                  >
                    {loadingPrice ? "Fetching Price..." : "Get Price"}
                  </button>
                </div>
              </div>
            </fieldset>
          </>
        )}

        <fieldset className="fieldset">
          <legend>Billing Items</legend>
          {(selectedBillingItems || []).map((selectedBillingItem, index) => (
            <div key={index} className="passengerRow">
              <div className="formGroup">
                <label>Billing Item</label>
                <CustomSelect
                  options={(billingItems || []).map((billingItem) => ({
                    value: billingItem.billingItemId,
                    label: billingItem.name,
                  }))}
                  value={selectedBillingItem.billingItemId}
                  onChange={(selectedOption) =>
                    handleBillingItemChange(
                      index,
                      "billingItemId",
                      selectedOption?.value || ""
                    )
                  }
                  placeholder="Select a billing Item"
                />
              </div>
              <div className="formGroup">
                <label>Cost</label>
                <input
                  type="number"
                  value={selectedBillingItem.cost}
                  onChange={(e) =>
                    handleBillingItemChange(index, "cost", e.target.value)
                  }
                  className="input"
                />
              </div>
              <div className="formGroup">
                <div className="file-upload-section">
                  <div className="file-label-container">
                    <label>Receipt</label>
                    {selectedBillingItem.imagePath && (
                      <div className="file-preview">
                        <button
                          type="button"
                          className="view-receipt-btn"
                          onClick={() =>
                            fetchFile(selectedBillingItem.imagePath)
                          }
                        >
                          Receipt
                        </button>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    className="input"
                    onChange={(e) => handleFileUpload(e.target.files[0], index)}
                  />
                </div>
              </div>
              <div className="formGroup">
                <label>Notes</label>
                <input
                  type="textarea"
                  value={selectedBillingItem.notes}
                  onChange={(e) =>
                    handleBillingItemChange(index, "notes", e.target.value)
                  }
                  className="input"
                />
              </div>
              <button
                type="button"
                onClick={() => removeBillingItem(index)}
                className="remove-button"
              >
                x
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addBillingItem}
            className="custom-button"
          >
            + Add Billing Item
          </button>
        </fieldset>

        <div className="row">
          <div className="formGroup">
            <label>Remarks</label>
            <textarea
              name="remarks"
              defaultValue={tripInfo?.remarks}
              className="input remarks"
              rows="3"
            />
          </div>
          <div className="formGroup">
            <label>Operator Notes</label>
            <textarea
              name="operatorNotes"
              defaultValue={tripInfo?.operatorNotes}
              className="input operatorNotes"
              rows="3"
            />
          </div>
        </div>

        <fieldset className="fieldset">
          <legend>Labels</legend>
          <div className="formGroup">
            <label>Select Labels</label>
            <CustomSelect
              options={(labels || []).map((label) => ({
                value: label.id,
                label: label.name,
              }))}
              value=""
              onChange={(selectedOption) => {
                if (
                  selectedOption?.value &&
                  !selectedLabelIds.includes(selectedOption.value)
                ) {
                  setSelectedLabelIds([
                    ...selectedLabelIds,
                    selectedOption.value,
                  ]);
                }
              }}
              placeholder="Select a Label"
            />

            {/* Display selected labels */}
            {selectedLabelIds.length > 0 && (
              <div className="selectedLabels">
                {(selectedLabelIds || []).map((id) => {
                  const label = (labels || []).find((label) => label.id === id);
                  if (!label) return null;

                  const backgroundColor = label.color;
                  const textColor = getContrastColor(backgroundColor);

                  return (
                    <div
                      key={id}
                      className="selectedLabel"
                      style={{ backgroundColor: label.color, color: textColor }}
                    >
                      {label.name}
                      <button
                        style={{ color: textColor }}
                        onClick={() => handleRemoveLabel(id)}
                      >
                        x
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </fieldset>

        {/* Submit Button */}
        <button
          type="submit"
          className="submitButton"
          disabled={isLoading("submitForm")}
        >
          {isLoading("submitForm") ? (
            <LoadingSpinner
              size="small"
              inline={true}
              message="Processing..."
              showMessageBelow={true}
            />
          ) : (
            "Book Trip"
          )}
        </button>
      </form>
    </div>
  );
};

export default AddBookedTrip;
